import { PLATFORM_HOME_LAYOUT } from "../platform-home/Config";
import { SPACE_HOME_LAYOUT } from "../space/home/Config";

/** 全屏底色层：fixed 铺满视口，z-index 低于 #root，专门负责非安全区着色 */
const BODY_BG_LAYER_ID = "lf-h5-body-bg-layer";

declare global {
  interface Window {
    __lfH5RepaintShellBg?: () => void;
    __lfH5ApplyViewport?: () => void;
  }
}

export const PAGE_SHELL_DEFAULT_TITLE = "龙粉社";

export const PAGE_SHELL_BG = {
  /** 平台首页：黑色 */
  platformHome: PLATFORM_HOME_LAYOUT.page.background,
  /** 空间主页 Hero 顶色（头像区背景渐变起始色） */
  spaceHome: SPACE_HOME_LAYOUT.hero.gradientFrom,
  /** 其余页面：白色 */
  spaceLight: "#ffffff",
  login: "#ffffff",
} as const;

export type PageShellFg = "#000000" | "#ffffff";

export interface PageShellBgState {
  bg: string;
  front: PageShellFg;
  title?: string | null;
}

let activeShellBg: PageShellBgState = {
  bg: PAGE_SHELL_BG.platformHome,
  front: "#ffffff",
  title: null,
};

/** 按 pathname 推断非安全区背景（Tab 切换 / 路由跳转时 App 层同步用） */
export function resolveShellBgFromPathname(pathname: string): Pick<PageShellBgState, "bg" | "front"> {
  if (!pathname || pathname === "/") {
    return { bg: PAGE_SHELL_BG.platformHome, front: "#ffffff" };
  }
  if (pathname.startsWith("/login") || pathname.startsWith("/legal/")) {
    return { bg: PAGE_SHELL_BG.login, front: "#000000" };
  }
  if (/^\/space\/\d{8}\/home\/?$/.test(pathname)) {
    return { bg: PAGE_SHELL_BG.spaceHome, front: "#ffffff" };
  }
  if (/^\/space\/\d{8}\//.test(pathname)) {
    return { bg: PAGE_SHELL_BG.spaceLight, front: "#000000" };
  }
  return { bg: PAGE_SHELL_BG.platformHome, front: "#ffffff" };
}

function ensureBodyBgLayer(): HTMLElement {
  let el = document.getElementById(BODY_BG_LAYER_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = BODY_BG_LAYER_ID;
    el.setAttribute("aria-hidden", "true");
    el.style.cssText =
      "position:fixed;top:0;left:0;right:0;bottom:0;z-index:0;pointer-events:none;margin:0;padding:0;border:0;";
    document.body.prepend(el);
  }
  return el;
}

/** 动态修改 body / html / 全屏底色层 */
export function paintPageShellBg(): void {
  const { bg, front } = activeShellBg;
  const html = document.documentElement;
  const body = document.body;
  if (!body) {
    return;
  }

  html.style.setProperty("--lf-h5-shell-bg", bg);
  html.style.setProperty("--lf-h5-shell-fg", front);
  html.style.backgroundColor = bg;

  body.style.backgroundColor = bg;
  body.style.color = front;

  const layer = ensureBodyBgLayer();
  layer.style.backgroundColor = bg;
}

export function applyPageShellBg(state: PageShellBgState): void {
  activeShellBg = {
    bg: state.bg,
    front: state.front,
    title: state.title !== undefined ? state.title : activeShellBg.title,
  };
  paintPageShellBg();
  if (state.title !== undefined) {
    document.title = state.title ?? "";
  }
}

export function repaintPageShellBg(): void {
  paintPageShellBg();
}

export function getActivePageShellBg(): PageShellBgState {
  return activeShellBg;
}

if (typeof window !== "undefined") {
  window.__lfH5RepaintShellBg = repaintPageShellBg;
}
